import comm from "csi/comm";
import Aspera, { HstDataTransfer } from "csi/comm/Aspera";
import JsonResponse from "csi/comm/JsonResponse";
import Request from "csi/comm/Request";
import locale from "csi/locale";
import csi from "csi/main";
import { ConversationUserProvider } from "csi/middleware/ConversationUserProvider";
import * as commentModel from "csi/model/comment";
import { getCommentRevisionCount, PinColor, PinData, PinPosition, threeDInfoExists } from "csi/model/comment";
import { DOCUMENT_REVISION } from "csi/model/document";
import monitor from "csi/monitor/_base";
import { getClass } from "csi/reflection/reflection";
import { getFileUploadRequest, getImageFileData, getPublishedFileData } from "csi/service/fileService";
import { submitWriter } from "csi/service/writerService";
import sessionConstants from "csi/sessionConstants";
import { C8Node, cnl, DUMMY, IFileData } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { getLocationForUrl } from "csi/utility/routerUtility";
import xml from "csi/xml";
import ChangeNode from "csi/xml/ChangeNode";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import { getMentionedUsers, MENTION_USER_URL } from "designSystem/components/editor/tributeService";
import { ComponentItem } from "designSystem/types/component-item-types";
import { flatten, intersection, sortBy, values, without } from "lodash";
import { action, autorun, computed, makeObservable, observable, runInAction } from "mobx";
import { featureTogglesStore } from "./rootStore";
import WorkingCache from "./WorkingCache";

export interface CommentSummary {
	category: string;
	content: string;
	hasAttachments: boolean;
	isResolved: boolean;
	lastReplyUrl?: cnl;
	numberOfReplies: number;
	participants: C8Node[];
	pinName: string;
	relatedHref?: string;
	relatedName?: string;
	relatedType?: string;
	revisionCount?: number;
	statusDate: string;
	statusUser: string;
	subject: string;
	threeDInfo: string;
}

export interface CommentDetail {
	attachments: IFileData[];
	author: C8Node;
	authorHref: string;
	authorName: string;
	content: string;
	date: string;
	editable: boolean;
	editing: boolean;
	pinName: string;
	revisionCount?: number;
	revisionUrl?: cnl;
	subject: string;
	threeDInfo: string;
	url: cnl;
}

export interface ResultItem extends Partial<Pick<CommentDetail, "author" | "authorName" | "pinName" | "revisionUrl" | "subject" | "threeDInfo" | "url">> {
	/** indicate if this result is either comment or user */
	isComment: boolean;

	/** indicate if this result is search entry */
	isSearchEntry?: boolean;

	/** indicate text for search entry */
	searchText?: string;
}

type FetchCallback = (response?: JsonResponse) => void;
const RELATED_OWNER_PATH = xml.path(xml.ATTR_PARENT, commentModel.ATTR_NAMED_PARENT);
const RESOLVED_STATE = commentModel.createStatusEnum(true);

let _allCommentCategories: C8Node[];
let _deletingSelected = false;
let _expectedNumberOfReplies = -1;

/** convert the comment category node to Item */
function commentCategoryToItem(commentCategory: C8Node): ComponentItem {
	return {
		id: commentCategory.$URL,
		name: locale.getNodeName(commentCategory),
	};
}

/** return last comment date for container node */
function getContainerDate(container: C8Node): number {
	const { LastChildCommentDate, LastCommentDate } = container;
	return LastChildCommentDate || LastCommentDate || 0;
}

/** make request to attach files to given comment */
async function requestAttachFiles(commentUrl: cnl, fileData: IFileData[], asperaData: HstDataTransfer[]): Promise<JsonResponse> {
	if (!fileData?.length) {
		return;
	}

	if (asperaData?.length) {
		for (const data of asperaData) {
			await Aspera.uploadSelectedFile(commentUrl, data, null);
		}

		return null;
	}

	const files = fileData.map(({ file }) => file);
	const request = getFileUploadRequest(files, null, commentUrl, "Images", "Files", false, false, true);
	return await request.go();
}

class CacheState {
	@observable clientTime = 0;
	@observable serverTime = 0;

	get isStale(): boolean {
		return this.clientTime < this.serverTime;
	}

	@action makeClientCurrent = (): void => {
		this.clientTime = csi.getCurrentTime();
	};

	@action makeCurrent = (): void => {
		this.clientTime = this.serverTime = csi.getCurrentTime();
	};

	@action reset = (): void => {
		this.clientTime = this.serverTime = 0;
	};

	@action updateServer = (time: number): void => {
		// don't allow the server time to go backwards so the user does not see there are changes
		// and then there are not; it's possible the server time is less than before if a comment is deleted
		this.serverTime = Math.max(time, this.serverTime);
	};

	constructor() {
		makeObservable(this);
	}
}

export default class CommentsStore extends ReactionStore {
	private readonly ALL = "SelectAll";
	private readonly NONE = "SelectNone";
	private _pinInitialPosition: PinPosition;

	@observable private _disabledNewConversation = false;
	@observable private _documentRevisionUrlInView: cnl;
	@observable private _editUrl: cnl;
	@observable private _enterSearchMode: boolean;
	@observable private _filesInProgress: IFileData[];
	@observable private _filterCategory: cnl = "";
	@observable private _finishInitialization: boolean = false;
	@observable private _isAuthorSearch: boolean = false;
	@observable private _isDeleted = false;
	@observable private _isLoading = false;
	@observable private _isNew = false;
	@observable private _isPinAllowed = false;
	@observable private _isReply = false;
	@observable private _pins: PinData[];
	@observable private _newPin: PinData;
	@observable private _pinColor: PinColor;
	@observable private _pinCount: string = "";
	@observable private _pinEditMode: boolean = false;
	@observable private _pinIsMoving: boolean = false;
	@observable private _replyPinButtonActive: boolean = false;
	@observable private _retryCallback: FetchCallback;
	@observable private _retryRequest: Request;
	@observable private _searchText = "";

	cache: WorkingCache = new WorkingCache();

	/** extension point for on add pin action to trigger reactions in 3D store  */
	onAddPin?: (data: PinData) => void;

	/** extension point for cancel new pin action to trigger reactions in 3D store  */
	onCancelNewPin?: (data: CommentDetail[]) => void;

	/** extension point for remove pin action to trigger reactions in 3D store  */
	onRemovePin?: (data: PinData) => void;

	/** list of all comments for current location */
	@observable.ref private allCommentUrls = [];

	/** list of comments that satisfy category and text filters */
	@observable.ref private commentUrls: cnl[] = [];

	/** url of node owning comments (could be that of primary or secondary class) */
	readonly containerUrl: cnl;

	/** if true then filter out resolved comments */
	@observable hideResolved = false;

	/** time of last client change to comments on this container */
	private listState = new CacheState();

	/** path from container url to primary type (could be empty) */
	readonly pathToPrimary: string;

	/** type of domain object owning comments */
	readonly primaryType: string;

	/** url of domain object owning comments */
	readonly primaryUrl: string;

	/** related types if any */
	readonly relatedTypes: string[];

	/** handles to websocket event listeners */
	private selectedCommentHandles: dojo.Handle[];

	/** Force update the annotation store to update the viewer pin color */
	@observable forcePinColorUpdate: boolean = false;

	/** time of last client change to comments on this container */
	@observable private selectedCommentState: CacheState;

	/** reply urls to selected comment; track separately as this can diverge from comment.Replies as other users reply */
	@observable selectedCommentReplyUrls: cnl[];

	/** selected comment url */
	@observable selectedCommentUrl: cnl;

	/** the being mentioned user */
	@observable.ref selectedUserToMention: C8Node;

	/** hovered comment url */
	@observable hoveredCommentUrl: cnl | null = null;

	/** if true then query for comments related to current location */
	@observable showRelated = true;

	/** sort order for comments; if not by latest, then sort by original comment date */
	@observable sortByLatestReply = true;

	/** Product Source of the Supplier */
	private productSourceUrl: cnl = null;

	/** indicate if it's allowed to switch the mentioned user in the editor */
	#okToSwitchMentionUser = true;

	/** returns all current pins to be displayed*/
	@computed get availablePinData(): CommentDetail[] {
		const {
			getCommentDetail,
			isCurrentRevision,
			isPinAllowed,
			optionsForSearch,
			_documentRevisionUrlInView,
		} = this;

		let data = [];

		if (!isPinAllowed) {
			return data;
		}

		const nodeURLs = [];

		// Get all pin URLs
		for (const parentComment of optionsForSearch) {
			if (parentComment.isComment) {
				nodeURLs.push(parentComment.url);
				nodeURLs.push(...this.cache.getPathNode(parentComment.url).Replies);
			}
		}

		data = nodeURLs.map(getCommentDetail);

		if (_documentRevisionUrlInView !== DUMMY) {
			data = data.filter((obj) => isCurrentRevision(obj.revisionUrl));
		}

		return data.filter((obj) => threeDInfoExists(obj.threeDInfo));
	}

	/** indicate if user can be able to reply */
	get canReply(): boolean {
		const { getCommentDetail, isEditing, selectedCommentUrl } = this;
		const commentDetail = getCommentDetail(selectedCommentUrl);
		const insufficientRights = (commentDetail?.author.$Type == "Node");
		return !isEditing && !insufficientRights;
	}

	/** all comment urls whose category matches the filter category (if one) */
	@computed get categoryCommentUrls(): cnl[] {
		const { allCommentUrls, cache, _filterCategory } = this;

		if (!_filterCategory) {
			return allCommentUrls;
		}

		return allCommentUrls.filter((commentUrl) => {
			const { Category } = cache.get(commentUrl);
			return _filterCategory === Category;
		});
	}

	/** paths to comments of other domain objects */
	get commentPaths(): string[] {
		const { pathToPrimary, primaryType } = this;
		const paths = commentModel.getResolvedCommentPaths(primaryType);
		return pathToPrimary ? paths.map((path) => xml.path(pathToPrimary, path)) : paths;
	}

	/** indicate if done with initialization */
	get finishInitialization(): boolean {
		return this._finishInitialization;
	}

	/** returns if pin is placed on the model */
	@computed get isPinOnModel(): boolean {
		if (this.newPin) {
			const { position: { x, y, z } } = this.newPin;
			return !(x == 0 && y == 0 && z == 0);
		} else {
			return false;
		}
	}

	/** returns incremented pin count  */
	get pinCount(): string {
		return this._pinCount;
	}

	/** returns initial position of pin before its set so a drag can be cancelled */
	get pinInitialPosition(): PinPosition {
		return this._pinInitialPosition;
	}

	/** returns all pins in current conversation array */
	get pins(): PinData[] {
		return this._pins;
	}

	/** returns if pin is in update mode to show update buttons*/
	get pinEditMode(): boolean {
		return this._pinEditMode;
	}

	/** returns pin is in movable state while adding new pin or updating pin position*/
	get pinIsMoving(): boolean {
		return this._pinIsMoving;
	}

	/** returns reply section pin button mouse over state */
	get replyPinButtonActive(): boolean {
		return this._replyPinButtonActive;
	}

	/** return pin data for comment being created or edited */
	get newPin(): PinData {
		return this._newPin;
	}

	/** comment container of the location node, for example, we'll be displaying comments on quotes, then container here would be based on style */
	get containerOfInstanceNode(): C8Node {
		return this.cache.get(this.containerUrl);
	}

	/** category to filter by */
	get filterCategory(): string {
		return this._filterCategory || this.ALL;
	}

	/** comment categories to filter by */
	@computed get filterCategoryItems(): ComponentItem[] {
		const { getAllItem, usedCategoryItems } = this;
		return [getAllItem(), ...usedCategoryItems.map(commentCategoryToItem)];
	}

	/** indicates if there are comments on this container */
	get hasComments(): boolean {
		return !!this.numberOfComments || !!this.listState.serverTime;
	}

	/** indicates if search is to be performed on authors */
	get isAuthorSearch(): boolean {
		return this._isAuthorSearch;
	}

	/** returns whether or not comments filter for hybrid user */
	get isCommentsFilterForHybridUser(): boolean {
		const { primaryType } = this;
		if (featureTogglesStore.COMMENTS_FILTER_FOR_HYBRID_USERS &&
			sessionConstants.userSupplierUrl &&
			(primaryType === "Style" || primaryType === "Material" || primaryType === "Food" || primaryType === "FormulatedMaterial")) {
			return true;
		} else {
			return false;
		}
	}

	/** returns whether or not New Conversation are allowed */
	get isDisabledNewConversation(): boolean {
		return this._disabledNewConversation;
	}

	/** indicates if container can also contain documents */
	get isDocumentContainer(): boolean {
		return getClass(this.primaryType).isDocAndCommentOwner;
	}

	/** check if in middle of comment or reply, and if so, then return confirmation message to display */
	get isEditing(): boolean {
		const { _editUrl, _isDeleted, _isNew, _isReply } = this;
		return !_isDeleted && (_isNew || _isReply || !!_editUrl);
	}

	/** indicates if server request failed */
	get isError(): boolean {
		return !!this._retryCallback || !!this._retryRequest;
	}

	/** indicates if there are files currently being uploaded to server */
	get isFileUploadInProgress(): boolean {
		return !!this._filesInProgress?.length;
	}

	/** indicates if comment list is filtered by category, text, or user */
	get isFiltered(): boolean {
		return !!this._filterCategory || !!this.searchText;
	}

	/** indicates if selected comment has been deleted by another user */
	get isSelectedCommentDeleted(): boolean {
		return this._isDeleted;
	}

	/** indicates if list of comments is stale */
	get isListStale(): boolean {
		return this.listState.isStale;
	}

	/** indicates server request for comments is in progress */
	get isLoading(): boolean {
		return this._isLoading;
	}

	/** indicates if user is in progress of creating new comment */
	get isNewComment(): boolean {
		return this._isNew;
	}

	/** returns whether or not pins are allowed */
	get isPinAllowed(): boolean {
		return this._isPinAllowed;
	}

	/** indicates if user is in progress of replying to a comment */
	get isReply(): boolean {
		return this._isReply && !this.selectedCommentResolved;
	}

	/** indicates if selected comment cache is stale */
	get isSelectedCommentStale(): boolean {
		return !!this.selectedCommentState?.isStale;
	}

	/** mention user template to populate the editor */
	@computed get mentionUserText(): string {
		const { cache, isReply, selectedComment, selectedUserToMention } = this;

		if (isReply) {
			const author: C8Node = selectedUserToMention || cache.get(selectedComment.Author);
			const { $URL, $Name } = author;

			return `<span contenteditable="false" class="fr-deletable fr-tribute" ${MENTION_USER_URL}="${$URL}"><a class="browse" href="` + getLocationForUrl($URL) + `">@${$Name}&nbsp;</a></span>`;
		}

		return "";
	}

	/** default category value for new comment form */
	@computed get newCategory(): string {
		const { NONE, _filterCategory, newCategoryItems } = this;
		const filterItem = newCategoryItems.find(({ id }) => id === _filterCategory);
		return filterItem?.id ?? NONE;
	}

	/** categories allowed for new comments */
	@computed get newCategoryItems(): ComponentItem[] {
		const { getEmptyItem, getValidCategoryItems } = this;
		return [getEmptyItem(), ...getValidCategoryItems(false, true)];
	}

	/** returned the color and position of the pin to be saved to node
	 * Documentation for pin versions:
	 * if there is no version: this used for old pins that were created before modelViewer was created
	 * version 2: used for new pins that have since been created using modelViewer
	*/
	get threeDInfo(): string {
		const { newPin } = this;
		if (newPin) {
			const { color, position } = newPin;
			return JSON.stringify({ color, position, version: 2 });
		}

		// TODO: "null" matches what is checked in Java - probably should just be ""
		return "null";
	}

	/** number of all comments for the current location */
	get numberOfComments(): number {
		return this.allCommentUrls.length || 0;
	}

	/** number of comments satisfying current search filter for the current location */
	get numberOfFilteredComments(): number {
		return this.commentUrls.length || 0;
	}

	/** comments satisfying current search filters for the current location */
	get filteredComments(): string[] {
		return this.commentUrls;
	}

	/** return all possible options for search that match the current category */
	@computed get optionsForSearch(): ResultItem[] {
		// NOTE: this assumes client has all results
		const { categoryCommentUrls, getCommentDetail } = this;
		const userMap: Record<string, C8Node> = {};
		const options: ResultItem[] = [];

		categoryCommentUrls.forEach((commentUrl) => {
			const detail = getCommentDetail(commentUrl);

			if (detail) {
				const { author } = detail;

				if (!userMap[author.$URL]) {
					userMap[author.$URL] = author;
				}

				options.push({
					isComment: true,
					...detail,
				});
			}
		});

		// add the users as well
		values(userMap)
			.forEach((author) => {
				const { $URL, $Name } = author;
				options.push({
					author,
					url: $URL,
					isComment: false,
					authorName: $Name,
					subject: "",
				});
			});
		return options;
	}

	/** returns 3d pin color  */
	get pinColor(): PinColor {
		return this._pinColor;
	}

	/** Set the force pin color update value to trigger the pin color change in model viewer */
	@action setForcePinColorUpdate = (value: boolean): void => {
		this.forcePinColorUpdate = value;
	};

	/** changes pin color on dropdown pin color selection */
	@action onChangePinColor = (color: PinColor): void => {
		this.setPinColor(color);
		this.setForcePinColorUpdate(true);
		if (this.newPin) {
			this._newPin.color = color;
		}
	};

	/** return items for pin color dropdown */
	@computed get pinColorCategoryItems(): ComponentItem[] {
		return (Object.entries(PinColor)
			.map(([name, id]) => ({ name, id })));
	}

	/** categories referenced by comments */
	@computed get usedCategoryItems(): C8Node[] {
		const { allCommentUrls, cache } = this;
		const comments = cache.getNodes(allCommentUrls);
		const hash = csi.hashArray(comments, "Category", Boolean);

		// remove empty category
		delete hash[csi.EMPTY_URL];

		return cache.getNodes(Object.keys(hash));
	}

	/** path to primary node from comment node */
	get pathToPrimaryFromComment(): string {
		return xml.path(xml.ATTR_PARENT, this.pathToPrimary);
	}

	/** url of the node containing the list of recent comments */
	get recentCommentContainerUrl(): cnl {
		const { containerUrl, primaryUrl, relatedTypes } = this;
		return relatedTypes.length ? primaryUrl : containerUrl;
	}

	/** get the search status, if true, then search mode */
	get searchFocused(): boolean {
		return this._enterSearchMode;
	}

	/** search text applied to comment subject or author */
	get searchText(): string {
		return this._searchText;
	}

	/** selected comment to show detail of */
	get selectedComment(): C8Node {
		return this.cache.get(this.selectedCommentUrl);
	}

	/** return selected comment's name, if one */
	get selectedCommentName(): string | null {
		return this.selectedComment?.$Name;
	}

	/** return the selected comment's state if any */
	get selectedCommentResolved(): boolean {
		return this.selectedComment?.Status == RESOLVED_STATE;
	}

	/** return the ProductSource  of the hybrid user */
	get productSourceNode(): C8Node {
		return this.cache.get(this.productSourceUrl);
	}


	/** search request to get comments based on current settings */
	@computed get searchRequest(): Search | null {
		const { CONTAINER_COMMENTS, PATH_DOCUMENTS_COMMENTS, ATTRIBUTES: { Author, Category, DateAttr, LastDate, Status } } = commentModel;

		const {
			_filterCategory,
			commentPaths,
			containerUrl,
			hideResolved,
			isDocumentContainer,
			pathToPrimaryFromComment,
			relatedTypes,
			searchText,
			showRelated,
			sortByLatestReply,
			isAuthorSearch,
			productSourceUrl,
			isCommentsFilterForHybridUser,
		} = this;

		// if have related comments, then need to get owner node as well (which is assumed to be a DocAndCommentOwner)
		const ownerPath = relatedTypes.length ? RELATED_OWNER_PATH : pathToPrimaryFromComment;

		const search = new Search()
			.byType(commentModel.TYPE_COMMENT)
			// TODO - determine appropriate page limits to use when working with virtualized list
			//.setPageLimits(1, 10);
			.addDependencyPaths([
				ownerPath,
				"Child:Author;Participants;Category;Images",
				"Child:LastPost/Child:Images",
				"Child:Revision/Child:Master",
			])
			.addParameter("Fmt.Complete", "Ref");

		// formulate query
		const query = search.getQuery();

		// add supplier filter - if comments filter for hybrid user is true and primary type is style
		if (isCommentsFilterForHybridUser) {
			if (productSourceUrl) {
				search.setLimitPath(productSourceUrl, "Child:DocumentsAndComments/Child:Comments");
				search.setLimitPath(productSourceUrl, "Child:DocumentsAndComments/Child:Documents/Child:Comments");
				search.setLimitPath(productSourceUrl, "Child:Samples/Child:DocumentsAndComments/Child:Comments");
				search.setLimitPath(productSourceUrl, "Child:Samples/Child:DocumentsAndComments/Child:Documents/Child:Comments");
				search.setLimitPath(productSourceUrl, "Child:SupplierItems/Child:DocumentsAndComments/Child:Comments");
				search.setLimitPath(productSourceUrl, "Child:SupplierItems/Child:DocumentsAndComments/Child:Documents/Child:Comments");
			} else {
				return null;
			}
		} else {
			search.setLimitAttribute(containerUrl, CONTAINER_COMMENTS);
			if (isDocumentContainer) {
				search.setLimitPath(containerUrl, PATH_DOCUMENTS_COMMENTS);
			}

			if (showRelated) {
				for (const path of commentPaths) {
					search.setLimitPath(containerUrl, path);
				}
			}
		}

		if (_filterCategory) {
			query.RefAttr(Category, xml.OP_EQUALS, _filterCategory);
		}

		if (hideResolved) {
			query.Attr(Status, xml.OP_NOT_EQUALS, RESOLVED_STATE);
		}

		if (searchText) {
			const value = xml.getServerMatchValue(searchText);
			if (isAuthorSearch) {
				query.Node(xml.PROP_NAME, xml.OP_MATCHES, value, xml.path(Author));
			} else {
				var qOr = query.Or();
				qOr.Node(xml.PROP_NAME, xml.OP_MATCHES, value);
				qOr.Node(xml.PROP_NAME, xml.OP_MATCHES, value, xml.path(Author));
			}
		}

		search.getSortQuery()
			.SortByAttr(sortByLatestReply ? LastDate : DateAttr, null, null, true);

		return search;
	}

	/** return conversation user provider */
	@computed get userProvider(): ConversationUserProvider {
		const { primaryUrl, selectedCommentUrl, isReply } = this;
		return new ConversationUserProvider(primaryUrl, selectedCommentUrl, isReply);
	}

	/** request and store all comment categories */
	private fetchCommentCategories = async (): Promise<void> => {
		if (!_allCommentCategories) {
			try {
				const search = new Search()
					.byType(commentModel.TYPE_COMMENT_CATEGORY);
				const query = search.getSortQuery()
					.SortByAttr(xml.ATTR_NAME);
				const response = await search.getRequest()
					.go();
				_allCommentCategories = response.getResultNodes();
			} catch (err) {
				console.error("fetchCommentCategories: ", err);
				_allCommentCategories = [];
			}
		}

		for (const node of _allCommentCategories) {
			this.cache.add(node);
		}
	};

	/** request and store product source for supplier */
	private fetchProductSourceForSupplier = async (): Promise<void> => {
		const { containerUrl } = this;
		try {
			const search = new Search()
				.byType(commentModel.TYPE_PRODUCT_SOURCE)
				.setLimitPath(containerUrl, "Child:__Parent__/Child:ProductSourcing/Child:ProductSources");
			const query = search.getQuery();
			query.RefAttr("Supplier", xml.OP_EQUALS, sessionConstants.userSupplierUrl);
			const response = await search.getRequest()
				.go();
			const productSource = response?.getResultNode();
			this.productSourceUrl = productSource?.$URL;
			if (this.productSourceUrl) {
				this.cache.add(productSource);
			} else {
				runInAction(() => {
					this._disabledNewConversation = true;
				});
			}
		} catch (err) {
			console.error("fetchProductSourceForSupplier: ", err);
			this.productSourceUrl = null;
		}
	};

	/** fetch comments from server */
	fetchCommentUrls = (): void => {
		const { searchRequest, submitRequest } = this;
		if (searchRequest) {
			submitRequest(searchRequest.getRequest(), (response) => {
				const {
					isFiltered,
					listState,
					numberOfComments,
				} = this;
				const urls = response?.getResultUrls() || [];

				this.commentUrls = urls;

				if (!numberOfComments || !isFiltered) {
					this.allCommentUrls = urls;
				}

				if (urls.length) {
					listState.makeCurrent();
				} else {
					listState.reset();
				}
			}, true);
		}
	};

	/** cache latest data for selected comment */
	fetchSelectedComment = (): void => {
		const { getCommentRequest, selectedCommentUrl, submitRequest } = this;
		const request = getCommentRequest(selectedCommentUrl);

		submitRequest(request, (response) => {
			const comment = response.getResultNode();
			this._isDeleted = !comment;

			if (!this._isDeleted) {
				this.selectedCommentReplyUrls = [...comment.Replies];
				this.selectedCommentState.makeClientCurrent();
			}
		});
	};

	/** return the "All" item */
	private getAllItem = (): ComponentItem => {
		const { ALL } = this;
		return {
			id: ALL,
			name: locale.resource(ALL),
		};
	};

	/** return the empty item */
	private getEmptyItem = (): ComponentItem => {
		const { NONE } = this;
		return {
			id: NONE,
			name: "",
		};
	};

	/** sets the selected color on new pin and set the pin to mode mode */
	@action movePin = (): void => {
		this.setPinColor(this._newPin?.color);
		this.updatePin();
	};

	/** updates current pin by removing and recreating the pin */
	@action updatePin = (bPreventPinEdit?: boolean): void => {
		if (!this.newPin) {
			return;
		}

		this._newPin.color = this.pinColor;
		this.removePin();
		this.addPin(true);
		if (!bPreventPinEdit) {
			this.setPinEditMode(true);
		}
	};

	/** sets whether or not pins are allowed */
	@action setAllowPins = (value: boolean): void => {
		this._isPinAllowed = value;
	};

	/** get all attachments of a comment */
	getAllCommentAttachments = async (commentUrl: cnl): Promise<IFileData[] | []> => {
		const { cache, getAttachments, getCommentRequest, submitRequest } = this;
		const request = getCommentRequest(commentUrl);

		await submitRequest(request);

		const comment = cache.get(commentUrl);
		if (!comment) {
			return [];
		}

		const replies = cache.getNodes(comment.Replies);
		const commentAttachments = getAttachments(comment, false);
		const repliesAttachments = flatten(replies.map((r) => getAttachments(r, false)));

		return sortBy([...commentAttachments, ...repliesAttachments], [(a): string => a.name.toLocaleUpperCase()]);
	};

	/** return attachments of individual comment node */
	getAttachments = (comment: C8Node, editable: boolean): IFileData[] => {
		const { cache, _filesInProgress } = this;
		const { Files, Images } = comment;
		let attachments = [];

		for (const [key, value] of Object.entries(Files as Record<string, string>)) {
			attachments.push(getPublishedFileData(key, value));
		}

		for (const imageUrl of Images) {
			const image = cache.get(imageUrl);
			attachments.push(getImageFileData(image));
		}

		if (editable && _filesInProgress) {
			attachments = [...attachments, ..._filesInProgress];
		}

		return sortBy(attachments, [(a): void => a.name.toLocaleUpperCase()]);
	};

	/** return detail information about individual comment node */
	getCommentDetail = (commentUrl: cnl): CommentDetail | null => {
		const {
			_editUrl,
			cache,
			finishInitialization,
			getAttachments,
			isCurrentRevision,
			selectedComment,
			selectedCommentResolved,
		} = this;

		if (!finishInitialization) {
			return null;
		}

		const { userUrl } = sessionConstants;
		const comment = cache.get(commentUrl);

		// Comments in revisions that are being purged will be null because they’re being deleted.
		if (!comment) {
			return null;
		}

		const { $Name, Author, Comment, PinName, Revision, ThreeDInfo } = comment;
		const author = cache.get(Author) || xml.nodeCreateClientNode(Author, null, locale.resource("AccessViolation"));
		const authorHref = getLocationForUrl(Author);
		const authorName = author.$Name;
		const date = commentModel.getDate(comment);
		const editing = _editUrl === commentUrl;
		const revisionCount = getCommentRevisionCount(comment, cache);

		// the last check for node access is required in case the node is frozen, or for some reason author's edit rights have been revoked
		const editable = !editing &&
			!selectedCommentResolved &&
			(selectedComment?.LastPost === commentUrl) &&
			(Author === userUrl) &&
			isCurrentRevision(Revision);

		const attachments = getAttachments(comment, editable);

		return {
			attachments,
			author,
			authorHref,
			authorName,
			content: Comment,
			date,
			pinName: PinName,
			revisionCount,
			revisionUrl: Revision,
			subject: $Name,
			threeDInfo: ThreeDInfo,
			url: commentUrl,
			editable,
			editing,
		};
	};

	/** return request needed to get single comment and all related nodes */
	getCommentRequest = (url: cnl): Request => {
		return comm.getNodeRequest(url)
			.addDependencyPaths([
				"Child:Author;Images",
				"Child:Replies/Child:Author;Images",
				"Child:Replies/Child:Revision/Child:Master",
			]);
	};

	/** return summary information about top-level comment at the given index */
	getCommentSummary = (commentUrl: cnl): CommentSummary => {
		const { cache, containerUrl } = this;

		const comment = cache.get(commentUrl);
		const {
			$Name,
			Category,
			Comment,
			HasAttachments,
			LastPost,
			Participants,
			PinName,
			Replies,
			Status,
			StatusUser,
			ThreeDInfo,
			__Parent__,
		} = comment;

		const category = locale.getNodeConfiguredName(cache.get(Category), cache);
		const lastReplyUrl = (LastPost === commentUrl) ? null : LastPost;
		const statusUser = cache.get(StatusUser);
		const revisionCount = getCommentRevisionCount(comment, cache);

		let relatedHref: string;
		let relatedName: string;
		let relatedType: string;

		if (__Parent__ !== containerUrl) {
			const related = cache.getPathNode(comment, RELATED_OWNER_PATH);
			if (related) {
				relatedHref = getLocationForUrl(related.$URL);
				relatedName = locale.getNodeConfiguredName(related, cache);
				relatedType = locale.type(related.$Type);
			}
		}

		return {
			category,
			content: Comment,
			hasAttachments: HasAttachments,
			isResolved: Status === RESOLVED_STATE,
			lastReplyUrl,
			numberOfReplies: Replies.length,
			participants: cache.getNodes(Participants),
			pinName: PinName,
			relatedHref,
			relatedName,
			relatedType,
			revisionCount,
			statusDate: commentModel.getStatusDate(comment),
			statusUser: statusUser?.$Name,
			subject: $Name,
			threeDInfo: ThreeDInfo,
		};
	};

	/** return category items that apply to the primary type, and optionally related types */
	private getValidCategoryItems = (showRelated = false, activeOnly: boolean = false): ComponentItem[] => {
		const { primaryType, relatedTypes } = this;

		const filteredCategories = activeOnly ? _allCommentCategories.filter((c) => c.Active) : _allCommentCategories;

		return filteredCategories.filter((node) => {
			const { ApplicableTypes } = node;
			return (ApplicableTypes.includes(primaryType) ||
				(showRelated && intersection(ApplicableTypes, relatedTypes).length)
			);
		})
			.map(commentCategoryToItem);
	};

	/** return true if comment revision matches current revision in view */
	private isCurrentRevision = (commentRevisionUrl: cnl): boolean => {
		return !xml.isValidURL(commentRevisionUrl) || (commentRevisionUrl === this._documentRevisionUrlInView);
	};

	/** creates a new pin on button click */
	@action addPin = (bIgnoreCount?: boolean): void => {
		this.setPinIsMoving(true);
		if (!bIgnoreCount) {
			this.resetPinCount();
		}
		this.setNewPin(bIgnoreCount);
		this.setPinEditMode(true);
		this.onAddPin?.(this.newPin);
	};

	/** send server request to attach files to comment */
	@action attachFiles = async (commentUrl: cnl, fileData: IFileData[]): Promise<void> => {
		const {
			beginTransactionOnSelected,
			endTransactionOnSelected,
			updateCacheState,
		} = this;

		let response: JsonResponse;

		this._filesInProgress = fileData;

		try {
			beginTransactionOnSelected();
			response = await requestAttachFiles(commentUrl, fileData, null);
		} finally {
			runInAction(() => {
				if (response) {
					updateCacheState(response);
				}

				endTransactionOnSelected();
				this._filesInProgress = null;
			});
		}
	};

	/** apply the filter */
	@action filterByItem = (item: ComponentItem): void => {
		const { id } = item;
		this._filterCategory = id === this.ALL ? "" : id;
	};

	/** exit mode of creating a new comment or viewing/editing/replying to selected comment */
	@action backToComments = (): void => {
		const { cancelNewPin, fetchCommentUrls, isListStale, resetEditState, _isDeleted } = this;

		resetEditState();

		if (isListStale || _isDeleted) {
			fetchCommentUrls();
		}

		cancelNewPin();
	};

	/** cancel creation or editing of a comment */
	@action cancel = (): void => {
		this._isNew = false;
		this._editUrl = null;
		this.cancelNewPin();
	};

	/** cancel any new pin operation and exit the editmode */
	@action cancelNewPin = (): void => {
		const { newPin } = this;
		this.setPinIsMoving(false);

		if (newPin?.count === "") {
			this.removePin(true);
			//after removing new pin set previous annotation pins back
			this.onCancelNewPin(this.availablePinData);
		} else {
			this._newPin = null;
		}
	};

	/**
	 * on cancel exits from pin edit mode and sets the pin location
	 */
	@action cancelUpdatePin = (): void => {
		this.setPinPosition(this.pinInitialPosition);
		this.updatePin();
		this.setPinIsMoving(false);
	};


	/** close error dialog after saving a new or edited comment */
	@action closeErrorDialog = (): void => {
		const { cancel, fetchSelectedComment, selectedComment } = this;

		cancel();

		// if there was an error while trying to edit an existing comment, then it could
		// be that the reply was deleted; in such a case refresh
		if (selectedComment) {
			fetchSelectedComment();
		}
	};

	/** reset state when closing the comments panel */
	@action closePanel = (): void => {
		this.resetListState();
		this.resetEditState();
	};

	/**
	 * on save exits from pin edit mode and sets the pin location
	 */
	@action saveUpdatePin = (): void => {
		this.setPinIsMoving(false);
	};

	/** returns new pin data */
	@action setNewPin = (bRetainPosition?: boolean): void => {
		const position = (bRetainPosition && this._newPin.position) || { x: 0, y: 0, z: 0 };
		const pinCount = !bRetainPosition ? this.pinCount : this._newPin?.count;
		const { _pinInitialPosition, pinEditMode, pinIsMoving } = this;

		if (!_pinInitialPosition && !pinEditMode && pinIsMoving && bRetainPosition) {
			this._pinInitialPosition = position;
		}

		this._newPin = {
			color: this.pinColor,
			commentUrl: this.containerOfInstanceNode?.$URL || xml.getClientNodeUrl(),
			count: pinCount,
			position,
		};
	};

	/** remove the current active pin */
	@action removePin = (bResetNewPin?: boolean): void => {
		this.onRemovePin?.(this.newPin);
		if (bResetNewPin) {
			this._newPin = null;
		} else if (!this.newPin && !this.isEditing) {
			this.setNewPin();
		}
	};

	/** sets pin color on dropdown */
	@action setPinColor = (value: PinColor): void => {
		this._pinColor = value;
	};

	/** sets pin position add add new id */
	@action setPinPosition = (pos: PinPosition): void => {
		if (this._newPin) {
			this._newPin.position = pos;
		}
	};

	/** stores active state for reply pin buttons container while navigating away from comments text editor*/
	@action setReplyPinButtonActive = (bActive: boolean): void => {
		this._replyPinButtonActive = bActive;
	};

	/** reset pin count to unknown for new pins*/
	@action resetPinCount = (): void => {
		this._pinCount = "";
	};

	/** sets pin in movable mode while adding new pin or updating pin position */
	@action setPinIsMoving = (val: boolean): void => {
		this._pinIsMoving = val;
		if (!val) {
			this.setPinEditMode(false);
			this._pinInitialPosition = null;
		}
	};

	/** sets pin in edit mode */
	@action setPinEditMode = (val: boolean): void => {
		this._pinEditMode = val;
	};

	/** delete given attachment on the currently selected comment */
	deleteAttachment = async (commentUrl: cnl, fileData: IFileData): Promise<void> => {
		const {
			beginTransactionOnSelected,
			endTransactionOnSelected,
			updateCacheState,
			writeDeletedAttachments,
		} = this;

		const writer = new Writer();
		const cn = writer.changeNode(commentUrl);

		writeDeletedAttachments(cn, commentUrl, [fileData]);

		try {
			beginTransactionOnSelected();

			const response = await submitWriter(writer);

			runInAction(() => {
				updateCacheState(response);
			});
		} finally {
			endTransactionOnSelected();
		}
	};

	/** delete given comment */
	deleteComment = async (commentUrl: cnl): Promise<void> => {
		const {
			backToComments,
			beginTransactionOnSelected,
			endTransactionOnSelected,
			fetchSelectedComment,
			removeCommentUrl,
			selectedCommentUrl,
			selectedCommentReplyUrls,
			updateCacheState,
		} = this;

		const writer = new Writer();

		writer.deleteNode(commentUrl);

		try {
			beginTransactionOnSelected(false, true);

			const response = await submitWriter(writer);
			const deletedUrls = response.getDeletedUrls();

			runInAction(() => {
				updateCacheState(response);

				if (commentUrl === selectedCommentUrl) {
					removeCommentUrl(commentUrl);
					backToComments();
				} else if (!deletedUrls) {
					// the reply was already deleted, so need to refresh comment
					fetchSelectedComment();
				} else {
					this.selectedCommentReplyUrls = without(selectedCommentReplyUrls, commentUrl);
				}
			});
		} finally {
			endTransactionOnSelected();
		}
	};

	/** start editing a comment */
	@action editComment = (commentUrl: cnl): void => {
		this._editUrl = commentUrl;
		this.editExisitingPin(commentUrl);
	};

	/** Edit exisiting pin */
	@action editExisitingPin = (commentUrl: cnl): void => {
		const comment = this.cache.get(commentUrl);
		if (comment.ThreeDInfo && threeDInfoExists(comment.ThreeDInfo)) {
			const existingPinData = JSON.parse(comment.ThreeDInfo);
			existingPinData.commentUrl = commentUrl;
			existingPinData.count = comment.PinName;
			this._newPin = existingPinData;
		}
	};

	/** fetch latest comments if out of date */
	enterPanel = (): void => {
		if (this.isListStale) {
			this.fetchCommentUrls();
		}
	};

	/** apply resolved items filter or not */
	@action toggleHideResolved = (): void => {
		this.hideResolved = !this.hideResolved;
	};

	/** apply show related/all items filter or not */
	@action applyShowRelatedItem = (enable: boolean): void => {
		this.showRelated = enable;
	};

	/** apply SortByLatestReply filter or not */
	@action applySortByLatestReply = (enable: boolean): void => {
		this.sortByLatestReply = enable;
	};

	/** track number of expected replies on selected comment before starting change */
	private beginTransactionOnSelected = (bReply = false, bDelete = false): void => {
		_deletingSelected = bDelete;
		_expectedNumberOfReplies = (this.selectedCommentReplyUrls?.length || 0) + (bReply ? 1 : 0);
	};

	/** reset number of expected replies on selected comment after completing change */
	private endTransactionOnSelected = (): void => {
		_deletingSelected = false;
		_expectedNumberOfReplies = -1;
	};

	/** focus search */
	@action enterSearchMode = (): void => {
		this._enterSearchMode = true;
	};

	/** async processing to initialize store */
	private initialize = async (): Promise<void> => {
		const {
			cache,
			containerUrl,
			fetchCommentCategories,
			fetchCommentUrls,
			fetchProductSourceForSupplier,
			listState,
			onChangeContainer,
			primaryUrl,
			recentCommentContainerUrl,
			submitRequest,
			trackReaction,
			isCommentsFilterForHybridUser,
		} = this;

		if (isCommentsFilterForHybridUser) {
			await fetchProductSourceForSupplier();
		}

		// fetch all comment categories if not done already and add to cache
		await fetchCommentCategories();

		// ensure comment container is in cache
		if (containerUrl !== primaryUrl) {
			await submitRequest(comm.getNodeRequest(containerUrl));
		}

		// initialize timestamp for comments based on what is currently in cache
		const recentCommentContainer = cache.get(recentCommentContainerUrl);
		const serverDate = getContainerDate(recentCommentContainer);
		listState.updateServer(serverDate);

		// fetch comment urls anytime an observable changes that factors into the server request
		// TODO: wait to query for comments until needed - no longer need this to determine if we have comments
		trackReaction(autorun(fetchCommentUrls));

		// watch for changes to node in order to update list
		const changeHandle = monitor.onChangeNode(recentCommentContainerUrl, onChangeContainer);
		trackReaction(changeHandle.remove);
		runInAction(() => {
			this._finishInitialization = true;
		});
	};

	/** leave search */
	@action leaveSearchMode = (): void => {
		this._enterSearchMode = false;
		this._searchText = "";
	};

	/** comment container has a new or changed comment */
	@action onChangeContainer = async (node: C8Node): Promise<void> => {
		const { cache, containerOfInstanceNode, fetchCommentUrls, listState, numberOfFilteredComments, resetEditState } = this;
		const serverDate = getContainerDate(node);
		const revisionCount = (master: C8Node): void => master.Revisions?.length || 0;

		if (revisionCount(containerOfInstanceNode) > revisionCount(node)) {
			// if master's number of revisions has decreased, such as with Purge (and Reset) Revisions), then refresh everything
			cache.add(node);
			this.commentUrls = [];
			this.allCommentUrls = [];
			resetEditState();
			fetchCommentUrls();
		} else if ((serverDate && !numberOfFilteredComments) || (!serverDate && numberOfFilteredComments)) {
			// if server has comments but we don't, or server has no comments and we do,
			// then force refresh of comment list
			cache.add(node);
			fetchCommentUrls();
		} else {
			listState.updateServer(serverDate);
		}
	};

	/** react to change to selected comment */
	@action onChangeSelectedComment = (comment: C8Node): void => {
		// if in the process of making a change to the selected comment, then only look for new replies;
		// it is possible the current user is also making a reply, so would be looking for one more than expected
		const update = (_expectedNumberOfReplies === -1) || (comment.Replies.length > _expectedNumberOfReplies);
		if (update) {
			const { cache } = this;
			const { LastDate, LastPost } = comment;
			const lastPost = cache.get(LastPost);

			// it is possible that the change is coming due to change from same running instance, such as when editing an attachment image;
			// the client ends up getting the new nodes cached, although for that case the nodes are cached after the websocket response.
			// Either way, keeping this test in case the client already has the most up-to-date information.
			if (!lastPost || (lastPost.Date < LastDate)) {
				this.selectedCommentState.updateServer(LastDate);
			}
		}
	};

	/** handle the okToSwitchMentionUser flag while replying to the conversation */
	onContentChangeCallback = (content: string): void => {
		const el = document.createElement("html");
		el.innerHTML = content;

		const containerSpan: HTMLSpanElement = el.getElementsByTagName("span")?.[0];

		if (containerSpan?.hasAttribute(MENTION_USER_URL)) {
			const linkElement = el.querySelector("span a");
			linkElement?.parentNode.removeChild(linkElement);
		}

		// if there's nothing left other than the mention template, we allow user to switch the mention user
		const leftover = containerSpan?.innerText.trim();

		this.#okToSwitchMentionUser = !leftover;
	};

	/** track if selected comment is deleted, unless in the process of deleting the comment ourselves */
	@action onDeleteSelectedComment = (url: cnl): void => {
		if (!_deletingSelected) {
			this._isDeleted = true;
			this.removeCommentUrl(url);

			// this is needed in case last comment is deleted so that hasComments will return false
			if (!this.numberOfComments) {
				this.listState.reset();
			}
		}
	};

	/** remove comment url from cached lists of comments */
	@action removeCommentUrl = (commentUrl: cnl): void => {
		this.commentUrls = without(this.commentUrls, commentUrl);
		this.allCommentUrls = without(this.allCommentUrls, commentUrl);
	};

	/** reply the comment */
	replyComment = async (
		commentUrl: cnl,
		reply: string,
		addedFiles: IFileData[],
		asperaData: HstDataTransfer[],
	): Promise<void> => {
		const {
			beginTransactionOnSelected,
			commentUrls,
			endTransactionOnSelected,
			selectedCommentReplyUrls,
			sortByLatestReply,
			threeDInfo,
			updateCacheState,
			_documentRevisionUrlInView,
		} = this;

		const {
			TYPE_COMMENT_REPLY,
			ATTRIBUTES: { Author, Comment, Mentions, Revision, ThreeDInfo },
		} = commentModel;
		const { userUrl } = sessionConstants;
		const writer = new Writer();
		const replyUrl = comm.createUrl();

		//attach commentsUrl before ThreeDInfo as the operation needs parent comments url inorder to create the appropriate pin name
		writer
			.createNode(replyUrl, TYPE_COMMENT_REPLY, null)
			.changeAttribute(Author, xml.ATTR_REF, userUrl)
			.changeAttribute(Comment, xml.ATTR_STRING, reply)
			.changeAttribute(Mentions, xml.ATTR_REFSET, getMentionedUsers(reply))
			.changeAttribute(Revision, xml.ATTR_REF, _documentRevisionUrlInView)
			.changeAttribute(ThreeDInfo, xml.ATTR_STRING, threeDInfo);

		try {
			beginTransactionOnSelected(true);

			// attach the reply
			writer
				.callMethod("SiteAdmin", "AttachCommentReply")
				.addParameter(xml.PROP_URL_PARENT, xml.ATTR_REF, commentUrl)
				.addParameter(xml.PROP_URL, xml.ATTR_REF, replyUrl);

			const response = await submitWriter(writer);
			const attachFilesResponse = await requestAttachFiles(
				replyUrl,
				addedFiles,
				asperaData,
			);

			runInAction(() => {
				updateCacheState(response, attachFilesResponse);
				this.selectedCommentReplyUrls = [...selectedCommentReplyUrls, replyUrl];
				this._isReply = false;
				this.#okToSwitchMentionUser = true;

				if (sortByLatestReply) {
					this.commentUrls = [commentUrl, ...without(commentUrls, commentUrl)];
				}
			});
		} finally {
			this.removePin(true);
			endTransactionOnSelected();
		}
	};

	/** reset edit state which includes creating new comments, editing comments, and the selected comment */
	@action resetEditState = (): void => {
		const { cache, commentUrls, _isDeleted, selectedCommentUrl } = this;

		// if selected comment is deleted, then remove it from store
		if (selectedCommentUrl && _isDeleted) {
			cache.deleteNode(selectedCommentUrl);
			this.commentUrls = without(commentUrls, selectedCommentUrl);
		}

		this.selectedCommentHandles?.forEach((handle) => handle.remove());
		this.selectedCommentHandles = null;
		this.selectedCommentReplyUrls = null;
		this.selectedCommentState = null;
		this.selectedCommentUrl = null;
		this.selectedUserToMention = null;

		this._editUrl = null;
		this._isDeleted = false;
		this._isNew = false;
		this._isReply = false;

		//reset the color selection to yellow for 3d viewer comments pin
		this.setPinColor(PinColor.YELLOW);
	};

	/** reset list state which includes all search and filter options */
	@action resetListState = (): void => {
		this.hideResolved = false;
		this.showRelated = true;
		this.sortByLatestReply = true;
		this._enterSearchMode = false;
		this._filterCategory = "";
		this._searchText = "";
	};

	/** invoke retry request to see if it works this time */
	@action resetError = (): void => {
		const { _retryCallback, _retryRequest, submitRequest } = this;
		this._retryCallback = null;
		this._retryRequest = null;

		if (_retryRequest) {
			submitRequest(_retryRequest, _retryCallback, true);
		} else {
			_retryCallback();
		}
	};

	/** move conversation to the top */
	moveConversationToTop = (url: string): void => {
		const { allCommentUrls, commentUrls } = this;
		this.commentUrls = [url, ...without(commentUrls, url)];
		this.allCommentUrls = [url, ...without(allCommentUrls, url)];
	};

	/** save new comment */
	save = async (
		formData: FormData,
		addedFiles: IFileData[],
		deletedFiles: IFileData[] = [],
		asperaData: HstDataTransfer[],
	): Promise<void> => {
		const {
			NONE,
			_documentRevisionUrlInView,
			_editUrl,
			_filterCategory,
			_isNew,
			allCommentUrls,
			beginTransactionOnSelected,
			commentUrls,
			containerUrl,
			endTransactionOnSelected,
			isListStale,
			listState,
			moveConversationToTop,
			threeDInfo,
			selectedCommentUrl,
			updateCacheState,
			writeDeletedAttachments,
			productSourceUrl,
			productSourceNode,
		} = this;

		if (!_editUrl && !_isNew) {
			return;
		}

		const {
			TYPE_COMMENT,
			ATTRIBUTES: { Author, Category, Comment, Mentions, Revision, Subject, ThreeDInfo },
		} = commentModel;
		const { userUrl } = sessionConstants;
		const writer = new Writer();
		const subject = formData.get(Subject) as string;
		const newCategory = formData.get(Category);
		const category = newCategory === NONE ? "" : newCategory;
		const content = formData.get(Comment);
		const pinContent = threeDInfo;
		let url = _editUrl;
		let newCommentUrl = null;
		let cn: ChangeNode;

		// write save operations
		if (_editUrl) {
			cn = writer.changeNode(_editUrl);
		} else {
			newCommentUrl = comm.createUrl();

			cn = writer
				.createNode(newCommentUrl, TYPE_COMMENT, subject)
				.changeAttribute(Author, xml.ATTR_REF, userUrl)
				.changeAttribute(Revision, xml.ATTR_REF, _documentRevisionUrlInView);

			if (category) {
				cn.changeAttribute(Category, xml.ATTR_REF, category);
			}
		}

		cn.changeAttribute(ThreeDInfo, xml.ATTR_STRING, pinContent);
		cn.changeAttribute(Comment, xml.ATTR_STRING, content);
		cn.changeAttribute(
			Mentions,
			xml.ATTR_REFSET,
			getMentionedUsers(content.toString()),
		);

		if (_editUrl && deletedFiles.length) {
			writeDeletedAttachments(cn, _editUrl, deletedFiles);
		}

		try {
			// submit save operations
			beginTransactionOnSelected();

			if (newCommentUrl) {
				// attach the comment
				writer
					.callMethod("SiteAdmin", "AttachComment")
					.addParameter(xml.PROP_URL_PARENT, xml.ATTR_REF, (productSourceUrl ? productSourceNode.DocumentsAndComments : containerUrl))
					.addParameter(xml.PROP_URL, xml.ATTR_REF, newCommentUrl);
			}

			const response = await submitWriter(writer);

			// if creating new comment (not editing), then need to update list state to current date or else
			// it will become stale while waiting for attachments to upload
			if (!url && !isListStale) {
				runInAction(() => {
					listState.makeClientCurrent();
				});
			}

			url = url || newCommentUrl;

			const attachResponse: JsonResponse = await requestAttachFiles(
				url,
				addedFiles,
				asperaData,
			);

			this.cancelNewPin();

			runInAction(() => {
				if (_editUrl) {
					moveConversationToTop(selectedCommentUrl);
					// stop editing
					this._editUrl = null;
				} else if (_filterCategory && category !== _filterCategory) {
					// if category filter does not match the category for the new comment, then just clear the filter
					this._filterCategory = "";
				} else {
					// add node to front of existing list
					this.commentUrls = [url, ...commentUrls];
					this.allCommentUrls = [url, ...allCommentUrls];
				}

				this._isNew = false;
				updateCacheState(response, attachResponse);
			});
		} finally {
			endTransactionOnSelected();
		}
	};

	/** set hovered comment using cnl - used when a pin in ModelViewer is hovered over */
	@action setHoveredCommentByUrl = (url: cnl | null): void => {
		this.hoveredCommentUrl = url;
	};

	/** set selected comment and fetch comment details */
	selectCommentByIndex = async (index: number): Promise<void> => {
		const { commentUrls, selectCommentByUrl } = this;
		await selectCommentByUrl(commentUrls[index]);
	};

	/** set selected comment using cnl and fetch comment details */
	selectCommentByUrl = async (url: cnl): Promise<void> => {
		const {
			getCommentRequest,
			onChangeSelectedComment,
			onDeleteSelectedComment,
			submitRequest,
		} = this;
		const request = getCommentRequest(url);

		await submitRequest(request, (response) => {
			// track if the comment has been deleted by another user
			const comment = response.getResultNode();
			this._isDeleted = !comment;
			this.selectedCommentUrl = url;

			if (this._isDeleted) {
				this.removeCommentUrl(url);
			} else {
				this.selectedCommentReplyUrls = [...comment.Replies];
				this.selectedCommentState = new CacheState();
				this.selectedCommentState.makeClientCurrent();
				this.selectedCommentHandles = [
					monitor.onChangeNode(url, onChangeSelectedComment),
					monitor.onDeleteNode(url, onDeleteSelectedComment),
				];
			}
		});
	};

	/** set revision node that is currently in view */
	@action setRevisionNodeInView = (node: C8Node): void => {
		// NOTE: currently we only track document revisions, not any other
		this._documentRevisionUrlInView = (node?.$Type === DOCUMENT_REVISION) ? node.$URL : DUMMY;
	};

	/** set search text */
	@action setSearchText = (text: string, isAuthorSearch: boolean): void => {
		this._searchText = text;
		this._isAuthorSearch = isAuthorSearch;
	};

	/** apply operation to set the selected comment status */
	setSelectedCommentResolved = async (resolved: boolean): Promise<void> => {
		const {
			beginTransactionOnSelected,
			endTransactionOnSelected,
			moveConversationToTop,
			selectedCommentUrl,
			updateCacheState,
		} = this;

		const { ATTRIBUTES, createStatusEnum } = commentModel;
		const writer = new Writer();

		writer
			.changeNode(selectedCommentUrl)
			.changeAttribute(
				ATTRIBUTES.Status,
				xml.ATTR_ENUM,
				createStatusEnum(resolved),
			);

		try {
			beginTransactionOnSelected();

			const response = await submitWriter(writer);

			runInAction(() => {
				moveConversationToTop(selectedCommentUrl);
				updateCacheState(response);

				// user could be in the middle of replying, editing, so reset state
				this._isReply = false;
				this._editUrl = null;
			});
		} finally {
			endTransactionOnSelected();
		}
	};

	/** start creation of a comment */
	@action startNewComment = (): void => {
		this._newPin = null;
		this._isNew = true;
	};

	/** start replying to a comment */
	@action startReply = (): void => {
		this._isReply = true;
	};

	/** start replying to the specific comment */
	@action startReplySpecificComment = (userToMention: C8Node): void => {
		if (this.#okToSwitchMentionUser) {
			this._isReply = true;
			this.selectedUserToMention = userToMention;
		}
	};

	/** stop replying to a comment */
	@action stopReply = (): void => {
		this._isReply = false;
		this.selectedUserToMention = null;
		this.removePin(true);
	};

	/** send given request to the server and process response */
	@action private submitRequest = async (
		request: Request,
		callback?: FetchCallback,
		isLoading?: boolean,
	): Promise<JsonResponse> => {
		let response: JsonResponse;

		if (isLoading) {
			this._isLoading = true;
		}

		try {
			response = await request.go();
		} catch (ex) {
			console.error("submitRequest error: ", ex);
		}

		runInAction(() => {
			if (response) {
				this.cache.cacheResponse(response);
				callback?.(response);
			} else if (isLoading) {
				// if response not successful, then store parameters to retry later
				this._retryRequest = request;
				this._retryCallback = callback;
			}

			this._isLoading = false;
		});

		return response;
	};

	/** capture cache state prior to making change, returning function to call to update state after change */
	private updateCacheState = (...responses: JsonResponse[]): void => {
		const {
			cache,
			isListStale,
			isSelectedCommentStale,
			listState,
			selectedCommentState,
		} = this;

		responses?.forEach((response) => cache.cacheResponse(response));

		if (this.commentUrls.length) {
			if (!isListStale) {
				listState.makeClientCurrent();
			}

			// only update the selected state if not already stale
			if (selectedCommentState && !isSelectedCommentStale) {
				selectedCommentState.makeClientCurrent();
			}
		} else if (!isListStale) {
			// if no more comments, then reset state unless already stale
			listState.reset();
		}
	};

	/** write operation to delete files from commment */
	private writeDeletedAttachments = (
		cn: ChangeNode,
		commentUrl: cnl,
		files: IFileData[],
	): void => {
		const {
			ATTRIBUTES: { Files, Images },
		} = commentModel;
		const { cache } = this;
		const comment = cache.get(commentUrl);
		const removedFiles = [];
		const removedImages = [];

		for (const fileData of files) {
			const { name } = fileData;

			if (comment[Files][name]) {
				removedFiles.push(name);
			} else {
				const images = cache.getNodes(comment[Images]);
				const image = images.find(({ $Name }) => $Name === name);
				removedImages.push(image.$URL);
			}
		}

		if (removedFiles.length) {
			cn.removeAttributeKeys(Files, xml.ATTR_URLMAP, removedFiles);
		}

		if (removedImages.length) {
			cn.changeAttribute(Images, xml.ATTR_REFLIST, removedImages, false, true);
		}
	};

	constructor(primary: C8Node, containerUrl: cnl, revisionNodeInView?: C8Node, pathToPrimary = "") {
		super();
		makeObservable(this);

		const { cache, initialize, setPinColor, setRevisionNodeInView } = this;
		const { $Type, $URL } = primary;

		this.containerUrl = containerUrl;
		this.pathToPrimary = pathToPrimary;
		this.primaryType = $Type;
		this.primaryUrl = $URL;
		this.relatedTypes = commentModel.getRelatedTypes($Type);
		this._pins = [];

		// cache primary node and current user
		cache.add(primary);

		setPinColor(PinColor.YELLOW);
		setRevisionNodeInView(revisionNodeInView);

		// finish initialization and search for comments
		initialize();
	}
}
